import { useAddons } from '@/react/portainer/addons/queries/useAddons';
import { useRefreshCatalog } from '@/react/portainer/addons/queries/useRefreshCatalog';
import { useIsPureAdmin } from '@/react/hooks/useUser';
import { useSystemInfo } from '@/react/portainer/system/useSystemInfo';
import { useFIPSMode } from '@/react/portainer/settings/queries/useFIPSMode';

import { PageHeader } from '@@/PageHeader';
import { Loading } from '@@/Widget';
import { Alert } from '@@/Alert';

import { AddonsCards } from './AddonsCards';
import { AddonsRequirementsInfo } from './AddonsRequirementsInfo';
import { CatalogAlert } from './CatalogAlert';
import { RetryButton } from './RetryButton';

export function ListView() {
  const isFIPSMode = useFIPSMode();
  // Addons are excluded entirely in FIPS mode: the API is not mounted, so the
  // catalog is not requested and only the exclusion message is shown.
  const addonsListQuery = useAddons({ enabled: !isFIPSMode });
  const addonsList = addonsListQuery.data;
  const isPureAdmin = useIsPureAdmin();
  const refreshCatalog = useRefreshCatalog();
  const { data: systemInfo } = useSystemInfo();
  const addonsAvailable = systemInfo?.addonsAvailable ?? false;
  // Until system info loads, availability is unknown rather than unavailable.
  const addonsUnavailable = !!systemInfo && !addonsAvailable;
  // Only administrators receive catalogError, so no role check is needed here.
  const catalogError = addonsList?.catalogError;

  return (
    <>
      <PageHeader
        title="Add-ons"
        breadcrumbs={[{ label: 'Add-on management' }]}
        reload
        loading={refreshCatalog.isLoading}
        // In FIPS mode the refresh route is not mounted.
        onReload={
          isPureAdmin && !isFIPSMode ? () => refreshCatalog.mutate() : undefined
        }
      />

      <div className="row">
        <p className="col-sm-12 mb-0 text-sm text-[var(--text-muted-color)]">
          Discover tools that integrate seamlessly with your workflow and boost
          productivity.
        </p>
      </div>

      {isFIPSMode ? (
        <div className="row mt-4">
          <div className="col-sm-12">
            <Alert color="warn" title="Add-ons are not available in FIPS mode">
              Add-ons cannot be installed or managed while Portainer is running
              in FIPS mode.
            </Alert>
          </div>
        </div>
      ) : (
        <>
          <AddonsRequirementsInfo />

          {addonsUnavailable && (
            <div className="row mt-4">
              <div className="col-sm-12">
                <Alert
                  color="warn"
                  title="Add-ons require Portainer to run on Kubernetes"
                >
                  Add-ons install into the cluster Portainer is deployed in, so
                  they are unavailable when Portainer runs elsewhere. No
                  connected environment is required.
                </Alert>
              </div>
            </div>
          )}

          {catalogError && addonsAvailable && (
            <CatalogAlert
              catalogError={catalogError}
              // Only a live catalog entry carries a chart reference, so with
              // none of them carrying one, nothing shown came from the catalog.
              catalogLoaded={
                !!addonsList?.addons?.some(
                  (addon) => addon.record?.helmChartRepository
                )
              }
              isRetrying={
                addonsListQuery.isFetching || refreshCatalog.isLoading
              }
              onRetry={() => refreshCatalog.mutate()}
            />
          )}

          {renderAddons()}
        </>
      )}
    </>
  );

  function renderAddons() {
    // A failed request leaves no data to show, so explain it instead of
    // spinning until the page is reloaded.
    if (addonsListQuery.isError && !addonsList) {
      return (
        <div className="row mt-4">
          <div className="col-sm-12">
            <Alert color="error" title="Unable to load add-ons">
              <div className="flex flex-col items-start gap-2">
                <p className="mb-0">
                  Portainer could not list the add-ons. Try again, or check the
                  Portainer server logs if the problem continues.
                </p>
                <RetryButton
                  isRetrying={addonsListQuery.isFetching}
                  onRetry={() => addonsListQuery.refetch()}
                  data-cy="addons-retry-list"
                />
              </div>
            </Alert>
          </div>
        </div>
      );
    }

    if (!addonsList) {
      return <Loading />;
    }

    return (
      <AddonsCards
        catalog={addonsList}
        disabled={!addonsAvailable}
        catalogUnavailable={!!catalogError}
      />
    );
  }
}
